/**
 * Appointment Result Card Styles
 * Theme styles for appointment search result cards
 */

// Typography size constants
const FONT_SIZE_PRIMARY = '1.2em';
const FONT_SIZE_SECONDARY = '1em';
const FONT_SIZE_TERTIARY = '0.8em';

export default {
  // Card/Container styles
  resultCard: {
    marginBottom: '6px',
    cursor: 'pointer',
    transition: 'all 0.3s ease-in-out',
    '&:hover:not($resultCardSelected)': {
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
      borderColor: '#BDBDBD',
      backgroundColor: '#FAFAFA',
    },
  },
  resultCardMobile: {
    padding: '12px 0px',
    borderRadius: '8px',
  },
  resultCardDesktop: {
    padding: '12px 16px',
    borderRadius: '6px',
  },
  resultCardSelected: {
    backgroundColor: 'rgba(94, 53, 177, 0.04)',
    border: '2px solid #7340AA',
    boxShadow: '0 2px 8px rgba(94, 53, 177, 0.2)',
    padding: '20px !important', // Expand padding significantly when selected
    '&:hover': {
      boxShadow: '0 3px 10px rgba(94, 53, 177, 0.25)',
    },
  },
  resultCardUnselected: {
    backgroundColor: '#ffffff',
    border: '1px solid #E0E0E0',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
  },

  resultLayout: {
    display: 'grid',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
  },
  // Standard layout (with avatar)
  resultLayoutMobile: {
    gridTemplateColumns: '1fr .5fr 1fr',
    gap: '12px',
  },
  resultLayoutDesktop: {
    gridTemplateColumns: '1fr .5fr 1fr',
    gap: '16px',
  },

  resultLayoutConsultMobile: {
    gridTemplateColumns: '1fr .75fr 1fr',
    gap: '12px',
  },
  resultLayoutConsultDesktop: {
    gridTemplateColumns: '1fr .75fr 1fr',
    gap: '16px',
  },

  // Column styles - Balanced layout with avatar as anchor
  resultTimeColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    justifySelf: 'end', // Align to right edge (towards avatar)
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  resultAvatarColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    justifySelf: 'center',
  },
  resultProviderColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifySelf: 'start', // Align to left edge (from avatar)
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },

  // Typography styles - Responsive sizing with clamp()
  resultTime: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(1em, 2.5vw, 1.2em)', // Scale down to 1em minimum
    color: '#000000',
    lineHeight: 1.1,
    letterSpacing: '-0.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  resultDate: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 500,
    fontSize: 'clamp(0.85em, 2vw, 1em)', // Scale down to 0.85em minimum
    color: '#616161',
    lineHeight: 1.3,
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  resultProviderName: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 600,
    fontSize: 'clamp(0.7em, 2vw, 1em)', // Scale down to 0.7em minimum
    color: '#000000',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  resultLocation: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 500,
    fontSize: 'clamp(0.7em, 1.8vw, 0.8em)', // Scale down to 0.7em minimum
    color: '#616161',
    lineHeight: 1.3,
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },

  // Avatar styles
  resultAvatarMobile: {
    width: '48px',
    height: '48px',
    fontSize: '18px',
    flexShrink: 0,
    backgroundColor: '#7340AA',
  },
  resultAvatarDesktop: {
    width: '56px',
    height: '56px',
    fontSize: '20px',
    flexShrink: 0,
    backgroundColor: '#7340AA',
  },

  // Consult layout typography
  resultConsultDate: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(1em, 2.5vw, 1.2em)', // Scales 1em-1.2em
    color: '#000000',
    lineHeight: 1.2,
  },
  resultConsultTime: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 600,
    fontSize: 'clamp(0.85em, 2vw, 1em)', // Scales 0.85em-1em
    color: '#000000',
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  resultConsultLocation: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 500,
    fontSize: 'clamp(0.7em, 1.8vw, 0.8em)', // Scales 0.7em-0.8em
    color: '#616161',
    lineHeight: 1.3,
  },

  // Expanded text styles (when card is selected)
  resultProviderNameExpanded: {
    whiteSpace: 'normal',
    textOverflow: 'clip',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
  resultLocationExpanded: {
    whiteSpace: 'normal',
    textOverflow: 'clip',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
  resultTimeExpanded: {
    whiteSpace: 'normal',
    textOverflow: 'clip',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
  resultDateExpanded: {
    whiteSpace: 'normal',
    textOverflow: 'clip',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
  resultConsultTimeExpanded: {
    whiteSpace: 'normal',
    textOverflow: 'clip',
    overflow: 'visible',
    wordBreak: 'break-word',
  },
};
